/**
 * OAuth 2.0 Utility Functions for mk-auth Integration
 *
 * This module provides client-side OAuth utilities for:
 * - CSRF state generation and validation
 * - Token storage and retrieval from localStorage
 * - OAuth flow initiation
 * - JWT decoding (client-side, no verification)
 *
 * LocalStorage Keys:
 * - mk_auth_state: CSRF protection state parameter
 * - mk_auth_access_token: JWT access token
 * - mk_auth_refresh_token: JWT refresh token
 */

// LocalStorage key constants
const STATE_KEY = 'mk_auth_state';
const ACCESS_TOKEN_KEY = 'mk_auth_access_token';
const REFRESH_TOKEN_KEY = 'mk_auth_refresh_token';

/**
 * JWT payload structure after decoding
 * Contains standard OAuth/OIDC claims and MadKudu-specific claims
 */
export interface DecodedJWT {
  sub?: string; // User ID
  iss?: string; // Issuer
  aud?: string; // Audience (client_id)
  scope?: string; // Space-separated scopes
  exp?: number; // Expiration time (Unix timestamp)
  iat?: number; // Issued at (Unix timestamp)
  jti?: string; // JWT ID (for revocation)
  sessionId?: string; // Session ID for logout

  // User claims (scope-dependent)
  email?: string;
  name?: string;
  first_name?: string;
  last_name?: string;

  // MadKudu-specific claims
  mkClaims?: {
    permissions: Array<{
      _id: string;
      tenant: number;
      role: 'admin' | 'architect' | 'user' | 'salesUser';
      team?: string;
    }>;
    team: string;
    superKudu: boolean;
  };

  [key: string]: unknown;
}

/**
 * Generate a cryptographically secure random state string for CSRF protection.
 * The state is automatically saved to localStorage.
 *
 * @returns {string} A 64-character hexadecimal string
 *
 * @example
 * ```typescript
 * const state = generateState();
 * // Returns: "a1b2c3d4e5f6..." (64 chars)
 * // Automatically stored in localStorage as 'mk_auth_state'
 * ```
 */
export function generateState(): string {
  try {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    const state = Array.from(array, (byte) =>
      byte.toString(16).padStart(2, '0')
    ).join('');

    localStorage.setItem(STATE_KEY, state);
    return state;
  } catch (error) {
    console.error('[OAuth] Failed to generate state:', error);
    throw new Error('Failed to generate OAuth state parameter');
  }
}

/**
 * Initiate the OAuth 2.0 Authorization Code flow by redirecting to mk-auth.
 * Generates a CSRF state parameter and constructs the authorization URL.
 *
 * @param {string} redirectUri - The URI to redirect back to after authorization (e.g., 'http://localhost:3013/oauth/callback')
 * @throws {Error} If redirectUri is not provided or localStorage is unavailable
 *
 * @example
 * ```typescript
 * // Redirect user to mk-auth login
 * initiateOAuthFlow('http://localhost:3013/oauth/callback');
 * ```
 */
export function initiateOAuthFlow(redirectUri: string): void {
  if (!redirectUri) {
    throw new Error('redirectUri is required for OAuth flow');
  }

  // This function should only be called client-side
  if (typeof window === 'undefined') {
    throw new Error('initiateOAuthFlow can only be called client-side');
  }

  try {
    // Generate and store CSRF state
    const state = generateState();

    // Use client ID injected at build time via webpack DefinePlugin
    // The webpack plugin handles fallback based on BUILD_TARGET or MK_AUTH_CLIENT_ID env var
    const clientId = MK_AUTH_CLIENT_ID;
    const mkAuthUrl = getMkAuthUrlForOrigin(window.location.origin);
    const scope = 'openid profile email';

    // Construct OAuth authorization URL
    const params = new URLSearchParams({
      client_id: clientId,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope,
      state,
    });
    const authorizeUrl = `${mkAuthUrl}/oauth/authorize?${params.toString()}`;

    // Redirect to mk-auth
    window.location.href = authorizeUrl;
  } catch (error) {
    console.error('[OAuth] Failed to initiate OAuth flow:', error);
    throw error;
  }
}

/**
 * Retrieve the stored access token from localStorage.
 *
 * @returns {string | null} The access token string, or null if not found
 *
 * @example
 * ```typescript
 * const token = getStoredToken();
 * if (token) {
 *   // Use token for API requests
 *   fetch('/api/data', {
 *     headers: { Authorization: `Bearer ${token}` }
 *   });
 * }
 * ```
 */
/**
 * The mk-auth host a page on `origin` signs in through (RGI-3230). A page on
 * an hginsights host stays on hginsights hosts, so a user never touches
 * madkudu.com; madkudu/wisekudu pages keep today's hosts.
 */
export function getMkAuthUrlForOrigin(origin: string): string {
  let host = '';
  try {
    host = new URL(origin).hostname;
  } catch {
    host = '';
  }
  if (host.endsWith('.hginsights.com')) {
    return 'https://access-api.hginsights.com';
  }
  if (host.endsWith('.hginsights.info')) {
    return 'https://access-api-staging.hginsights.info';
  }
  if (host.endsWith('.madkudu.com')) {
    return 'https://access-api.madkudu.com';
  }
  return 'https://access-api.wisekudu.com';
}

export function getStoredToken(): string | null {
  try {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  } catch (error) {
    console.error('[OAuth] Failed to get stored token:', error);
    return null;
  }
}

/**
 * Store access and refresh tokens in localStorage.
 *
 * @param {string} accessToken - The JWT access token
 * @param {string} refreshToken - The JWT refresh token
 *
 * @example
 * ```typescript
 * // After successful token exchange
 * saveTokens(
 *   'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...',
 *   'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...'
 * );
 * ```
 */
export function saveTokens(accessToken: string, refreshToken: string): void {
  try {
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  } catch (error) {
    console.error('[OAuth] Failed to save tokens:', error);
    throw new Error('Failed to save tokens to localStorage');
  }
}

/**
 * Clear all OAuth-related data from localStorage.
 * Removes access token, refresh token, and state parameter.
 * Call this when logging out or when tokens are invalid.
 *
 * @example
 * ```typescript
 * // On logout
 * clearTokens();
 * window.location.href = '/login';
 * ```
 */
export function clearTokens(): void {
  try {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(STATE_KEY);
  } catch (error) {
    console.error('[OAuth] Failed to clear tokens:', error);
  }
}

/**
 * Decode a JWT token payload (client-side only, no verification).
 *
 * WARNING: This function does NOT verify the token signature.
 * It only decodes the payload for client-side inspection.
 * Token verification must be done server-side.
 *
 * @param {string} token - The JWT token string
 * @returns {DecodedJWT | null} The decoded payload object, or null if invalid
 *
 * @example
 * ```typescript
 * const token = getStoredToken();
 * if (token) {
 *   const payload = decodeJWT(token);
 *   if (payload) {
 *     console.log('User email:', payload.email);
 *     console.log('Token expires at:', new Date(payload.exp * 1000));
 *     console.log('User permissions:', payload.mkClaims?.permissions);
 *   }
 * }
 * ```
 */
export function decodeJWT(token: string): DecodedJWT | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.error('[OAuth] Invalid JWT format: expected 3 parts');
      return null;
    }

    // Get the payload (middle part)
    const payload = parts[1];

    // Base64 decode (handle URL-safe base64)
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));

    return JSON.parse(decoded) as DecodedJWT;
  } catch (error) {
    console.error('[OAuth] Failed to decode JWT:', error);
    return null;
  }
}

/**
 * Get the stored CSRF state from localStorage.
 * Used for state validation in OAuth callback.
 *
 * @returns {string | null} The stored state string, or null if not found
 * @internal This is primarily for OAuth callback validation
 */
export function getStoredState(): string | null {
  try {
    return localStorage.getItem(STATE_KEY);
  } catch (error) {
    console.error('[OAuth] Failed to get stored state:', error);
    return null;
  }
}

/**
 * Get the stored refresh token from localStorage.
 *
 * @returns {string | null} The refresh token string, or null if not found
 *
 * @example
 * ```typescript
 * const refreshToken = getStoredRefreshToken();
 * if (refreshToken) {
 *   // Use to refresh access token
 * }
 * ```
 */
export function getStoredRefreshToken(): string | null {
  try {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  } catch (error) {
    console.error('[OAuth] Failed to get stored refresh token:', error);
    return null;
  }
}
